import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler, getLoaderBtn, getModalSwitch } from '@app/store/actions';
import { ModalActionsComponent, PasslySelect, PasslyCheckbox } from '@app/components';
import { ITenantLicense } from '@app/models';
import { FormControl } from '@material-ui/core';
import { licenseAllocationService } from '@app/services';
import {
    defaultOrgId,
    translationVariable,
    onPressEnterModal,
    genericErrorString,
} from '@app/helpers';
import { InputNumber } from '@itglue/kaseya-ui';

export interface IReceivedData {
    orgId: string;
    orgName: string;
    hasLicense: boolean;
    seats: number;
    licenseEdition: string;
    active: number;
    passwordServerEnabled: boolean;
    tenantLicense: ITenantLicense;
    handleRefresh: Function;
}

const AllocateSeatsDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [licenseEditions, setLicenseEditions] = useState([]);
    const [licenseEdition, setLicenseEdition] = useState('');
    const [maxSeats, setMaxSeats] = useState(props.seats || 0);
    const [maxSeatsInvalid, setMaxSeatsInvalid] = useState(false);
    const [disableLicenseType, setDisableLicenseType] = useState(false);
    const [enablePws, setEnablePws] = useState(false);
    const [seatsRemaining, setSeatsRemaining] = useState(0);
    const [formValid, setFormValid] = useState(true);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        if (props.licenseEdition === null) {
            setLicenseEdition('NONE');
        } else {
            setLicenseEdition(props.licenseEdition);
        }

        setEnablePws(props.passwordServerEnabled);

        computeLicenseTypeOptions();
    }, []);

    useEffect(() => {
        computeDisableLicenseType();
        setSeatsRemaining(computeSeatsRemaining(licenseEdition));
    }, [licenseEdition]);

    useEffect(() => {
        if (maxSeatsInvalid) {
            setFormValid(false);
        } else {
            setFormValid(true);
        }
    }, [maxSeatsInvalid]);

    const computeLicenseTypeOptions = () => {
        let licenseEdition: any = [];

        licenseEdition.push({ label: 'NONE', value: 'NONE' });

        if (props.tenantLicense?.Entries?.length) {
            props.tenantLicense.Entries.forEach(function (entry) {
                const entryName: string = entry.Name;
                licenseEdition.push({ label: entryName, value: entryName });
            });
        }

        setLicenseEditions(licenseEdition);
    };

    const computeSeatsRemaining = (licenseEditionChosen: string) => {
        let seats: number = 0;

        if (licenseEditionChosen) {
            const licenseEditionEntry: any = props.tenantLicense?.Entries.filter(function (entry) {
                if (entry.Name === licenseEdition) {
                    return entry;
                }
            });

            if (licenseEditionEntry?.length) {
                if (props.hasLicense) {
                    let left: number = licenseEditionEntry[0].SeatsRemaining;

                    // if this orgs license belongs to the original licenseEdition put its Seats back into the seatRemaining.
                    if (licenseEditionEntry[0].Name === props.licenseEdition) {
                        left += props.seats;
                    }

                    if (left < 0) {
                        left = 0;
                    }

                    seats = left;
                } else {
                    seats = licenseEditionEntry[0].SeatsRemaining;
                }
            }
        }

        if (props.active > seats || maxSeats > seats) {
            let msg: string = translation('ORG_LICENSE_ACTIVE_EXCEEDS_MAX_SEATS_ALLOWED').replace(translationVariable, seats.toString());

            setErrorMessage(msg);
            setFormValid(false);
        } else {
            setErrorMessage('');
            setFormValid(true);
        }

        return seats;
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'licenseEdition':
                setLicenseEdition(value);
                break;
            case 'maxSeats':
                setMaxSeats(value);
                break;
            default:
                break;
        }
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const { name } = e.target;

        switch (name) {
            case 'enablePws':
                setEnablePws(checked);
                break;
            default:
                break;
        }
    };

    const onMaxSeatsChange = (e: any) => {
        setErrorMessage('');

        const maxSeatsChosen = e.value;
        setMaxSeats(maxSeatsChosen);

        if (maxSeatsChosen === '') {
            setMaxSeatsInvalid(true);
        } else if (maxSeatsChosen > seatsRemaining) {
            setMaxSeatsInvalid(true);

            let msg: string = translation('ORG_LICENSE_MAX_EXCEEDS_MAX_SEATS_ALLOWED').replace(translationVariable, seatsRemaining.toString());

            setErrorMessage(msg);
        } else if (maxSeatsChosen < props.active) {
            setMaxSeatsInvalid(true);

            setErrorMessage(translation('ORG_LICENSE_MAX_LESS_THAN_ORG_ACTIVE'));
        } else {
            setMaxSeatsInvalid(false);
        }
    };

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));

        dispatch(getLoaderBtn(false));
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const handleSuccess = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
        props.handleRefresh(true);
    };

    const handleSaveBtn = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        dispatch(getLoaderBtn(true));

        let jsonObject: any = {};
        jsonObject.licenseEdition = licenseEdition;
        jsonObject.maxSeats = maxSeats.toString();
        jsonObject.orgId = props.orgId;
        jsonObject.passwordServerEnabled = enablePws;

        licenseAllocationService.assignSeatsFeaturesToOrg(jsonObject, handleError, handleSuccess);
    };

    const computeDisableLicenseType = () => {
        if (props.orgId === defaultOrgId) {
            setDisableLicenseType(true);
            return;
        }

        setDisableLicenseType(false);
    };

    return (
        <>
            <form className='modal__form' style={{minHeight: '150px'}} method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !formValid)}>
                <FormControl>
                    <PasslySelect
                        name='licenseEdition'
                        value={licenseEdition}
                        onChange={onFormChange}
                        id='licenseEdition-input'
                        label={'LICENSE_EDITION'}
                        disabled={disableLicenseType}
                        options={licenseEditions}
                        required
                    />
                </FormControl>
                {licenseEdition !== 'NONE' && (
                    <span>
                        <div className='modal__body--row'>
                            <FormControl>
                                <InputNumber
                                    name='maxSeats'
                                    label={translation('MAX_SEATS')}
                                    value={maxSeats}
                                    onChange={onMaxSeatsChange}
                                    showButtons
                                    id='maxSeats-input'
                                    idecrementButtonClassName={''}
                                    min={0}
                                    max={seatsRemaining}
                                    errorMessage={maxSeats === null ? translation('REQUIRED_FIELD') : undefined}
                                    required
                                />
                            </FormControl>
                        </div>
                        <div className='modal__body--row'>
                            <br />
                            <div>
                                {translation('ACTIVE_SEATS')}: <b>{props.active}</b>
                            </div>
                        </div>
                        <div className='modal__body--row'>
                            <div>
                                {translation('AVAILABLE_SEATS')}: <b>{seatsRemaining}</b>
                            </div>
                        </div>
                        {props.orgId !== defaultOrgId && (
                            <div className='modal__body--row'>
                                <PasslyCheckbox
                                    name='enablePws'
                                    checked={enablePws}
                                    onChange={onCheckboxChange}
                                    label={translation('ENABLE_PASSWORD_SERVER')}
                                />
                            </div>
                        )}
                    </span>
                )}
            </form>

            {errorMessage && <div className='modal__error'>{errorMessage}</div>}

            <ModalActionsComponent
                primaryBtn={translation('SAVE_CHANGES')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={!formValid}
                id='allocate'
            />
        </>
    );
};

export default AllocateSeatsDialog;
